import { useEffect } from "react";

import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useEmailPattern } from "@ui/hooks/useEmailPattern";
import { useUser } from "@ui/hooks/useUser";
import { useTranslations } from "@ui/providers/useTranslations";
import { formatDate } from "@ui/utils/formatDate";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { UiCheckCircleIcon } from "@jti/brand-icons/ui-check-circle";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";

import { Dialog } from "../Dialog";
import { Controller, Form, useForm } from "../Form";
import { Select } from "../Inputs/Select";
import { TextInput } from "../Inputs/TextInput";
import { InternationalTelephone } from "../Telephone/InternationalTelephone";

import { useWithdrawalCandidates } from "./hooks/useWithdrawalCandidates";

import { isWithdrawalFormModalConfigured } from "./isWithdrawalFormModalConfigured";

import styles from "./WithdrawalFormModal.module.scss";

const { sanitize } = dompurify;

export type WithdrawalFormModalConfig = {
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  title?: string;
  description?: string;
  firstNameLabel?: string;
  lastNameLabel?: string;
  emailLabel?: string;
  phoneLabel?: string;
  phoneHelperText?: string;
  orderLabel?: string;
  orderHelperText?: string;
  itemSingularLabel?: string;
  itemPluralLabel?: string;
  successTitle?: string;
  successSectionTitle?: string;
  successFirstNameLabel?: string;
  successLastNameLabel?: string;
  successEmailLabel?: string;
  successPhoneLabel?: string;
  successOrderLabel?: string;
  successSubmittedOnLabel?: string;
  successContent?: string;
  successHelperText?: string;
};

export type WithdrawalFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  selectedOrderValue: string;
};

type WithdrawalFormState = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  selectedOrder: string;
};

const digitsOnlyFormatter = (value: string) => value.replace(/\D/g, "");

const formatWithdrawalOrderLabel = ({
  orderId,
  itemsCount,
  date,
  itemSingularLabel = "item",
  itemPluralLabel = "items",
}: {
  orderId: string;
  itemsCount: number;
  date: string;
  itemSingularLabel?: string;
  itemPluralLabel?: string;
}) => {
  const formattedOrderId = orderId.startsWith("#") ? orderId : `#${orderId}`;
  const itemsLabel = itemsCount === 1 ? itemSingularLabel : itemPluralLabel;
  const formattedDate = formatDate(date, "DD MMM YYYY", undefined, true);

  return `${formattedOrderId} / ${itemsCount} ${itemsLabel} / ${formattedDate}`;
};

export type WithdrawalFormModalProps = {
  onClose: () => void;
  step?: "confirm" | "success";
  onConfirm?: (values: WithdrawalFormValues) => void | Promise<void>;
  submittedOn?: string;
  isLoading?: boolean;
  error?: string | null;
  defaultSelectedOrderId?: string;
};

export const WithdrawalFormModal = ({
  onClose,
  step = "confirm",
  onConfirm,
  submittedOn = "",
  isLoading = false,
  error = null,
  defaultSelectedOrderId,
}: WithdrawalFormModalProps) => {
  const { breakpoint } = useDeviceBreakpoint();
  const isMobile = breakpoint === "mobile";
  const { __ } = useTranslations();
  const emailPattern = useEmailPattern();
  const { user, isUserLoggedIn } = useUser();
  const { withdrawalCandidates, isWithdrawalCandidatesLoading } = useWithdrawalCandidates();

  const withdrawalOrderConfig = useConfig().withdrawalOrderConfig;

  const form = useForm<WithdrawalFormState>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      selectedOrder: "",
    },
    mode: "onSubmit",
  });
  const { watch, control, setValue, reset } = form;
  const formValues = watch();

  useEffect(() => {
    if (!isUserLoggedIn || !user) {
      return;
    }

    reset(
      {
        firstName: user.first_name ?? "",
        lastName: user.last_name ?? "",
        email: user.email ?? "",
        phoneNumber: user.phone ?? "",
      },
      {
        keepDirtyValues: true,
      }
    );
  }, [isUserLoggedIn, reset, user]);

  useEffect(() => {
    if (!isUserLoggedIn || withdrawalCandidates.length === 0 || formValues.selectedOrder) {
      return;
    }

    const selectedOrderId = defaultSelectedOrderId || withdrawalCandidates[0]?.orderId;
    setValue("selectedOrder", selectedOrderId ?? "");
  }, [isUserLoggedIn, setValue, withdrawalCandidates, defaultSelectedOrderId, formValues.selectedOrder]);

  if (!isWithdrawalFormModalConfigured(withdrawalOrderConfig)) {
    return null;
  }

  const configuredWithdrawalOrderConfig = withdrawalOrderConfig;
  const withdrawalForm = configuredWithdrawalOrderConfig.withdrawalForm;
  const withdrawalSuccessContent = configuredWithdrawalOrderConfig.withdrawalSuccessContent;
  const withdrawalErrorTitle = configuredWithdrawalOrderConfig.withdrawalRequestErrorTitle;
  const withdrawalErrorLinkLabel = configuredWithdrawalOrderConfig.withdrawalOpenBannerLinkLabel;
  const withdrawalErrorLinkUrl = configuredWithdrawalOrderConfig.withdrawalOpenBannerLinkUrl;
  const withdrawalOrderOptions = withdrawalCandidates.map((candidate) => ({
    value: candidate.orderId,
    label: formatWithdrawalOrderLabel({
      ...candidate,
      itemSingularLabel: withdrawalForm.itemSingularLabel,
      itemPluralLabel: withdrawalForm.itemPluralLabel,
    }),
  }));

  return (
    <Dialog
      onClose={onClose}
      contentWrapperClassName={styles.withdrawalFormDialogContentWrapper}
      ariaLabelledBy="withdrawal-form-dialog-title"
      data-testid="withdrawal-form-dialog"
    >
      {step === "confirm" ? (
        <Form
          form={form}
          onSubmit={({ firstName, lastName, email, phoneNumber: submittedPhoneNumber, selectedOrder }) => {
            onConfirm?.({
              firstName,
              lastName,
              email,
              phoneNumber: submittedPhoneNumber,
              selectedOrderValue: selectedOrder,
            });
          }}
        >
          <div className={styles.content}>
            <h2 id="withdrawal-form-dialog-title" className={styles.title}>
              {withdrawalForm.title}
            </h2>
            <p className={styles.description}>{withdrawalForm.description}</p>

            <div className={styles.row}>
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: __("form_validation.field_required"),
                  maxLength: { value: 255, message: __("form_validation.max_length", ["255"]) },
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    label={withdrawalForm.firstNameLabel}
                    isError={!!fieldState.error}
                    errorText={fieldState.error?.message}
                    data-testid="withdrawal-form-first-name"
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: __("form_validation.field_required"),
                  maxLength: { value: 255, message: __("form_validation.max_length", ["255"]) },
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    label={withdrawalForm.lastNameLabel}
                    isError={!!fieldState.error}
                    errorText={fieldState.error?.message}
                    data-testid="withdrawal-form-last-name"
                  />
                )}
              />
            </div>

            <Controller
              name="email"
              control={control}
              rules={{
                required: __("form_validation.field_required"),
                pattern: {
                  value: emailPattern,
                  message: __("login.email.validation.field_pattern"),
                },
              }}
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label={withdrawalForm.emailLabel}
                  isError={!!fieldState.error}
                  errorText={fieldState.error?.message}
                  data-testid="withdrawal-form-email"
                />
              )}
            />

            <InternationalTelephone
              id="phoneNumber"
              label={withdrawalForm.phoneLabel}
              disclaimer={withdrawalForm.phoneHelperText}
              defaultCountry={undefined}
              onEditClick={() => {}}
            />

            <Controller
              name="selectedOrder"
              control={control}
              rules={{ required: __("form_validation.field_required") }}
              render={({ field, fieldState }) =>
                isUserLoggedIn ? (
                  <Select
                    name={field.name}
                    label={withdrawalForm.orderLabel}
                    options={withdrawalOrderOptions}
                    value={withdrawalOrderOptions.find((option) => option.value === field.value) ?? null}
                    onChange={(option) => field.onChange(option?.value ?? "")}
                    onBlur={field.onBlur}
                    helperText={withdrawalForm.helperText}
                    isError={!!fieldState.error}
                    errorText={fieldState.error?.message}
                    isHelperTextVisible={!!fieldState.error || !!withdrawalForm.helperText}
                    isDisabled={isWithdrawalCandidatesLoading}
                    dataTestId="withdrawal-form-order-select"
                  />
                ) : (
                  <TextInput
                    {...field}
                    label={withdrawalForm.orderLabel}
                    type="tel"
                    inputMode="numeric"
                    formatter={digitsOnlyFormatter}
                    helperText={withdrawalForm.helperText}
                    isError={!!fieldState.error}
                    errorText={fieldState.error?.message}
                    data-testid="withdrawal-form-order"
                  />
                )
              }
            />

            {error && (
              <AlertBanner
                tone="error"
                title={withdrawalErrorTitle}
                description={error}
                actions={
                  withdrawalErrorLinkLabel && withdrawalErrorLinkUrl
                    ? [{ label: withdrawalErrorLinkLabel, href: withdrawalErrorLinkUrl, nativeBehaviour: true }]
                    : undefined
                }
                role="alert"
              />
            )}

            <div className={styles.buttonsWrapper}>
              <Button
                className={styles.withdrawalButton}
                type="submit"
                variant="primary"
                size={isMobile ? "sm" : "lg"}
                disabled={isLoading}
              >
                {withdrawalForm.confirmButtonLabel}
              </Button>
              <Button
                className={styles.withdrawalButton}
                onClick={onClose}
                variant="secondary"
                size={isMobile ? "sm" : "lg"}
              >
                {withdrawalForm.cancelButtonLabel}
              </Button>
            </div>
          </div>
        </Form>
      ) : (
        <div className={styles.successContent}>
          <h2 id="withdrawal-form-dialog-title" className={styles.successTitle}>
            {withdrawalSuccessContent.title}
          </h2>
          <UiCheckCircleIcon className={styles.successIcon} aria-hidden="true" />

          <div className={styles.successSection}>
            <h3 className={styles.successSectionTitle}>{withdrawalSuccessContent.subtitle}</h3>
            {withdrawalSuccessContent.content && (
              <div
                className={styles.successList}
                dangerouslySetInnerHTML={{
                  __html: sanitize(withdrawalSuccessContent.content),
                }}
              />
            )}
          </div>

          <dl className={styles.summary}>
            <div className={styles.summaryRow}>
              <dt>{withdrawalSuccessContent.successFirstNameLabel}</dt>
              <dd>{formValues.firstName}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>{withdrawalSuccessContent.successLastNameLabel}</dt>
              <dd>{formValues.lastName}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>{withdrawalSuccessContent.successEmailLabel}</dt>
              <dd>{formValues.email}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>{withdrawalSuccessContent.successPhoneLabel}</dt>
              <dd>{formValues.phoneNumber ?? ""}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>{withdrawalSuccessContent.successOrderLabel}</dt>
              <dd>{formValues.selectedOrder ?? ""}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>{withdrawalSuccessContent.successSubmittedOnLabel}</dt>
              <dd>{submittedOn}</dd>
            </div>
          </dl>

          {withdrawalSuccessContent.successHelperText && (
            <div
              className={styles.successInfoText}
              dangerouslySetInnerHTML={{
                __html: sanitize(withdrawalSuccessContent.successHelperText),
              }}
            />
          )}
        </div>
      )}
    </Dialog>
  );
};

export default WithdrawalFormModal;
